import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "£399",
    cadence: "one-off project",
    desc: "For new and small local businesses that need a professional presence fast.",
    features: [
      "Professional website",
      "Mobile optimisation",
      "Contact form",
      "Click-to-call",
      "WhatsApp integration",
    ],
    cta: "Get Started",
    popular: false,
  },
  {
    id: "growth",
    name: "Growth",
    price: "£699",
    cadence: "one-off project",
    desc: "For established businesses ready to be found on Google and capture more enquiries.",
    features: [
      "Everything in Starter",
      "Google Business Profile support",
      "Facebook & Instagram business setup",
      "Enquiry capture system",
      "Basic local SEO",
      "Google Search Console & indexing setup",
    ],
    cta: "Grow My Business",
    popular: true,
  },
  {
    id: "automation",
    name: "Automation",
    price: "From £999",
    cadence: "tailored project",
    desc: "For busy businesses that want enquiries handled and followed up automatically.",
    features: [
      "Everything in Growth",
      "Automated enquiry responses",
      "Lead follow-up workflows",
      "Booking / enquiry automation",
      "Custom business workflows",
      "CRM / lead management integration where appropriate",
    ],
    cta: "Book a Consultation",
    popular: false,
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 sm:py-28 bg-navy-900/40 border-y border-sky-500/10">
      <div className="section-shell">
        <Reveal>
          <p className="micro-label">Pricing</p>
          <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white max-w-2xl">
            Clear packages, honest pricing
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed">
            Choose the level that fits where your business is today. Every package is designed to help
            you get found, capture enquiries and automate growth.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={0.08 * i} className="h-full">
              <article
                data-testid={`pricing-card-${p.id}`}
                className={`relative h-full rounded-3xl p-8 flex flex-col card-hover ${
                  p.popular
                    ? "border-2 border-sky-400/70 bg-navy-800/80 shadow-[0_30px_80px_-30px_rgba(14,165,233,0.45)]"
                    : "border border-sky-500/12 bg-navy-900/70"
                }`}
              >
                {p.popular && (
                  <span data-testid="pricing-badge-popular" className="absolute -top-3.5 left-8 rounded-full bg-sky-500 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                    Most Popular
                  </span>
                )}
                <h3 className="font-heading text-xl font-bold text-white">{p.name}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-white whitespace-nowrap">{p.price}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500 whitespace-nowrap">{p.cadence}</span>
                </div>
                <ul className="mt-7 space-y-3.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-sky-400" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  data-testid={`pricing-cta-${p.id}`}
                  className={`${p.popular ? "btn-primary" : "btn-ghost"} mt-8 w-full`}
                >
                  {p.cta} <ArrowRight size={15} strokeWidth={2.5} />
                </a>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p data-testid="pricing-note" className="mt-8 text-center text-xs sm:text-sm text-slate-500">
            Final pricing depends on your project requirements — every quote is tailored to your business.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
